import { Alert, BadgeIcon, Button, Dialog, Group, Switch, Tooltip } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import ExternalLink from '~/app/shared/components/ExternalLink';
import { Field } from '~/app/shared/components/Form';
import { useFeatureFlags } from '~/app/shared/featureFlags';
import PaymentMethodBadge from '../PaymentMethodBadge';
import mastercard from '../../assets/mastercard-2x.png';
import visa from '../../assets/visa-2x.png';
import googlepay from '../../assets/googlepay-2x.png';
import applepay from '../../assets/applepay-2x.png';
import mobilepay from '../../assets/mobilepay-2x.png';

interface Props {
    prevStep: () => void;
    nextStep: () => void;
}

const PricePlan = ({ prevStep, nextStep }: Props) => {
    const { isEnabled } = useFeatureFlags();

    const onPrevClick = () => {
        prevStep();
    };

    const onNextClick = () => {
        nextStep();
    };

    return (
        <>
            <Dialog.Title>
                <Trans>Choose the price plan</Trans>
            </Dialog.Title>

            <div className="flex my-4">
                <Trans>Select the payment methods you want to add to your invoices.</Trans>
            </div>

            <div className="border border-grey-200 rounded-lg p-4 divide-y divide-grey-200">
                <section>
                    <div className="flex">
                        <strong>
                            <Trans>Fee: 1.5% + DKK 2.5 per transaction in DK and EU with private cards</Trans>
                        </strong>
                    </div>

                    <div className="flex my-4">
                        <Group>
                            <PaymentMethodBadge imgSrc={mastercard} alt="mastercard" />
                            <PaymentMethodBadge imgSrc={visa} alt="visa" />
                            <PaymentMethodBadge imgSrc={googlepay} alt="googlepay" />
                            <PaymentMethodBadge imgSrc={applepay} alt="applepay" />
                        </Group>
                    </div>
                </section>

                <section>
                    {!isEnabled('paymentLink2MobilePay') && (
                        <div className="flex mt-4">
                            <Alert state="information">
                                <Trans>
                                    MobilePay is coming soon! We're working hard to integrate it and will let you know as soon as
                                    it's ready.
                                </Trans>
                            </Alert>
                        </div>
                    )}

                    <div className="flex mt-4 align-center">
                        <span className="self-center pr-2">
                            <Field.Checkbox name="pricePlan" fieldClassName="flex">
                                {({ input: { name, value, onChange } }) => (
                                    <>
                                        <Switch
                                            id="mobilepay-switch-button"
                                            className="!mt-0 mr-2"
                                            name={name}
                                            checked={!!value}
                                            onChange={value => {
                                                onChange(Number(value));
                                            }}
                                            disabled={!isEnabled('paymentLink2MobilePay')}
                                        />
                                        <Trans>Add</Trans>
                                    </>
                                )}
                            </Field.Checkbox>
                        </span>
                        <PaymentMethodBadge imgSrc={mobilepay} alt="mobilepay" className="w-[80px]" />
                    </div>

                    <div className="flex flex-col my-4">
                        <strong>
                            <Trans>Price: 49 DKK/month. (MobilePay subscription)</Trans>
                            <Tooltip
                                title={t`The subscription and DKK 1 per transaction out of DKK 3.5 are charged by pensopay.
                                            You will soon receive an email from them with a link where you can quickly and
                                            securely enter your card details.
                                            You will be invoiced the full amount of the current month from the day you activate
                                            MobilePay regardless of the date, and invoiced again on the 1st of the next month`}>
                                <BadgeIcon small icon="info" color="blue" className="ml-1" />
                            </Tooltip>
                        </strong>
                        <strong>
                            <Trans>Fee: 1.5% + DKK 3.5 per transaction in DK and EU with private cards</Trans>
                        </strong>
                    </div>
                </section>
            </div>

            <div className="flex mt-10">
                <span className="flex items-center">
                    <span className="mr-1">
                        <Trans>Read more about the pricing</Trans>
                    </span>

                    <ExternalLink href="https://www.e-conomic.dk/support/artikler/opsaetning-af-betalingslink#section1">
                        <Trans>here.</Trans>
                    </ExternalLink>
                </span>
                <Group className="flex ml-auto">
                    <Button onClick={onPrevClick}>
                        <Trans>Back</Trans>
                    </Button>
                    <Button appearance="primary" onClick={onNextClick}>
                        <Trans>Next</Trans>
                    </Button>
                </Group>
            </div>
        </>
    );
};

export default PricePlan;
